import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useNextT } from "../../i18n";
import s from "./Pager.module.scss";

type Props = { page: number; lastPage: number; onChange: (page: number) => void; label: string; prevLabel: string; nextLabel: string };

/** Page numbers: first, last and a window of three around the current page (clamped at the ends); one hidden number is shown, not "…". */
export const pagerItems = (page: number, lastPage: number): (number | "gap-start" | "gap-end")[] => {
  const from = Math.max(1, Math.min(page - 1, lastPage - 2));
  const to = Math.min(lastPage, from + 2);
  const out: (number | "gap-start" | "gap-end")[] = [];
  if (from > 1) out.push(1);
  if (from === 3) out.push(2); else if (from > 3) out.push("gap-start");
  for (let p = from; p <= to; p++) out.push(p);
  if (to === lastPage - 2) out.push(lastPage - 1); else if (to < lastPage - 2) out.push("gap-end");
  if (to < lastPage) out.push(lastPage);
  return out;
};

export const Pager = ({ page, lastPage, onChange, label, prevLabel, nextLabel }: Props) => {
  const { isRtl } = useNextT();
  if (lastPage <= 1) return null;
  const flip = isRtl ? ` ${s.flip}` : "";
  return (
    <nav aria-label={label}>
      <ul className={s.list}>
        <li>
          <button type="button" className={`${s.pg} ${s.tap}${flip}`} aria-label={prevLabel} disabled={page <= 1} onClick={() => onChange(page - 1)}>
            <ChevronLeftRoundedIcon aria-hidden className={s.arrow} />
          </button>
        </li>
        {pagerItems(page, lastPage).map((it) => typeof it === "number" ? (
          <li key={it}>
            <button type="button" className={`${s.pg} ${s.tap}${it === page ? ` ${s.on}` : ""}`} aria-current={it === page ? "page" : undefined} onClick={() => onChange(it)}>{it}</button>
          </li>
        ) : (
          <li key={it} aria-hidden="true" className={s.gap}>…</li>
        ))}
        <li>
          <button type="button" className={`${s.pg} ${s.tap}${flip}`} aria-label={nextLabel} disabled={page >= lastPage} onClick={() => onChange(page + 1)}>
            <ChevronRightRoundedIcon aria-hidden className={s.arrow} />
          </button>
        </li>
      </ul>
    </nav>
  );
};
